import { useCallback, useEffect, useRef, useState } from 'react';

const LONG_PRESS_MS = 350;
const SCROLL_CANCEL_PX = 10;
const POST_TOUCH_GUARD_MS = 500;
const EMOJI_DATA_ATTR = 'data-reaction-emoji';

interface Args {
  onLongPressOpen: () => void;
  onSelect: (emoji: string) => void;
}

/**
 * Touch-side of the reaction picker (Facebook-style long-press → drag → release).
 *
 * Splits cleanly from the desktop hover flow:
 * - long-press the trigger to open the picker
 * - drag across emojis (each emoji must carry `data-reaction-emoji`) to highlight
 * - release on a highlighted emoji to select; release elsewhere keeps it open for tap
 *
 * `isPostTouch()` lets the desktop hover handlers ignore the synthetic mouse
 * events that browsers fire after a tap. `consumeLongPress()` lets click handlers
 * drop the synthetic click that follows a long-press.
 */
export const useReactionTouchPicker = ({ onLongPressOpen, onSelect }: Args) => {
  const [hoveredEmoji, setHoveredEmoji] = useState<string | null>(null);

  const longPressTimerRef = useRef<NodeJS.Timeout | null>(null);
  const longPressFiredRef = useRef(false);
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);
  const lastTouchAtRef = useRef(0);

  const cancelLongPressTimer = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => cancelLongPressTimer(), [cancelLongPressTimer]);

  const onTouchStart = (e: React.TouchEvent) => {
    const { clientX, clientY } = e.touches[0];
    lastTouchAtRef.current = Date.now();
    touchStartRef.current = { x: clientX, y: clientY };
    longPressFiredRef.current = false;

    cancelLongPressTimer();
    longPressTimerRef.current = setTimeout(() => {
      longPressFiredRef.current = true;
      onLongPressOpen();
    }, LONG_PRESS_MS);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    const { clientX, clientY } = e.touches[0];

    // Before the picker is open, treat any meaningful drag as a page scroll.
    if (!longPressFiredRef.current) {
      const start = touchStartRef.current;
      if (!start) {
        return;
      }
      const movedTooFar =
        Math.abs(clientX - start.x) > SCROLL_CANCEL_PX || Math.abs(clientY - start.y) > SCROLL_CANCEL_PX;
      if (movedTooFar) {
        cancelLongPressTimer();
      }
      return;
    }

    // Picker is open: highlight whichever emoji the finger is over.
    const target = document.elementFromPoint(clientX, clientY) as HTMLElement | null;
    const emoji = target?.closest<HTMLElement>(`[${EMOJI_DATA_ATTR}]`)?.dataset.reactionEmoji ?? null;
    setHoveredEmoji(emoji);
  };

  const onTouchEnd = () => {
    lastTouchAtRef.current = Date.now();
    cancelLongPressTimer();

    if (longPressFiredRef.current && hoveredEmoji) {
      onSelect(hoveredEmoji);
    }
    setHoveredEmoji(null);
    touchStartRef.current = null;
  };

  /** True for the ~500ms after a touch — desktop hover handlers should bail out. */
  const isPostTouch = () => Date.now() - lastTouchAtRef.current < POST_TOUCH_GUARD_MS;

  /** Returns true and resets the flag if the click is a synthetic click after a long-press. */
  const consumeLongPress = () => {
    if (longPressFiredRef.current) {
      longPressFiredRef.current = false;
      return true;
    }
    return false;
  };

  return {
    hoveredEmoji,
    isPostTouch,
    consumeLongPress,
    touchHandlers: { onTouchStart, onTouchMove, onTouchEnd, onTouchCancel: onTouchEnd },
  };
};
